import { useManageRolesStoreActions } from "PFApp/stores/manage_roles";
import { RolesFormKey } from "PFApp/types/activity_form/activity_form_schema";
import {
  ManageRolesFormType,
  ManageRolesMultiForm
} from "PFApp/types/activity_form/manage_roles_form_schema";
import { useEffect, useEffectEvent, useRef } from "react";
import { useFormContext } from "react-hook-form";

import { getNestedRoles } from "../manage_roles.utils";
import { useUnsavedEditsSyncContext } from "../parts/unsaved_edits_sync_context";
import { diffRoles } from "./use_form_unsaved_edits_tracker.utils";

type UseFormUnsavedEditsTracker = {
  rolesFormKey: RolesFormKey;
  trackedKeys: Set<string>;
  currentPage: number;
};

export const useFormUnsavedEditsTracker = ({
  rolesFormKey,
  trackedKeys,
  currentPage
}: UseFormUnsavedEditsTracker) => {
  const { subscribe, getValues } = useFormContext<ManageRolesMultiForm>();
  const { addUnsavedEdit, changeFailedEditsToUnsaved } = useManageRolesStoreActions();
  const { trackerResetVersionRef } = useUnsavedEditsSyncContext();

  const prevRolesRef = useRef<ManageRolesFormType[] | null>(null);
  const lastSeenVersionRef = useRef(trackerResetVersionRef.current);

  const handleFormChange = useEffectEvent((values: ManageRolesMultiForm) => {
    const prev = prevRolesRef.current;
    const next = getNestedRoles(values, rolesFormKey);

    if (prev === next) {
      return;
    }

    // A version bump means the form was programmatically reset (e.g. the query invalidation
    // triggered by an availability save repopulating the form). That change is server-driven,
    // not a user edit, so re-baseline without diffing to avoid creating spurious unsaved edits.
    // The null case is the initial population, which is handled the same way. Both branches
    // keep lastSeenVersionRef in sync so a later real edit is not mistaken for a reset.
    const isServerReset = trackerResetVersionRef.current !== lastSeenVersionRef.current;

    if (prev === null || isServerReset) {
      lastSeenVersionRef.current = trackerResetVersionRef.current;
      prevRolesRef.current = next ? structuredClone(next) : null;
      return;
    }

    const changes = diffRoles(prev, next ?? [], trackedKeys);
    if (changes.length === 0) {
      return;
    }

    changes.forEach(({ roleId, fieldName, nextValue, prevValue }) => {
      addUnsavedEdit(roleId, fieldName, nextValue, prevValue, currentPage);
    });

    new Set(changes.map(({ roleId }) => roleId)).forEach((roleId) => {
      changeFailedEditsToUnsaved(roleId);
    });

    prevRolesRef.current = next ? structuredClone(next) : null;
  });

  useEffect(() => {
    if (prevRolesRef.current === null) {
      const initialRoles = getNestedRoles(getValues(), rolesFormKey);
      prevRolesRef.current = initialRoles ? structuredClone(initialRoles) : null;
    }

    return subscribe({
      name: rolesFormKey,
      formState: { values: true },
      callback: ({ values }) => handleFormChange(values)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesFormKey]);
};
